import { consume } from '@lit/context';
import { t } from 'i18next';
import { css, html, LitElement, nothing, PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';

import {
  PrintOptionCollapsibleDto,
  PrintOptionCollapsibleStepDto,
  SelectedPrintOptionDto,
} from '@/api-sdk';
import { FgPersonalisation } from '@/components';
import {
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
  personalisation,
  PersonalisationType,
  visualDesigner,
  VisualDesignerContextType,
} from '@/context';
import { baseStyles } from '@/styles';
import {
  getPersonalisationData,
  getPrintOptionDisplayData,
  isScrolledIntoView,
  scrollStepIntoView,
} from '@/utils';

import '@/components/accordion/fg-accordion';
import '@/components/accordion/fg-accordion-footer';
import '@/components/accordion/fg-accordion-title';
import '@/components/accordion/fg-accordion-title-button';
import '@/components/configurator/step-collapsible/print-option/fg-collapsible-print-option';
import '@/components/fg-print-option-summary';
import '@/components/personalisation/fg-personalisation';
import '@/components/personalisation/fg-personalisation-status-bar';

@customElement('fg-print-option-stacked')
export class FgPrintOptionStacked extends LitElement {
  private fgPersonalisationDialogRef: Ref<FgPersonalisation> = createRef();

  @property({ type: Object })
  stepData?: PrintOptionCollapsibleStepDto;

  @property({ type: Boolean })
  showValidationMessage = false;

  @property({ type: Boolean })
  scrollToStep = false;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  @state()
  personalisationData?: PersonalisationType;

  @state()
  currentSelectedPrintOption?: SelectedPrintOptionDto;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext!: PersonalisationType[];

  @consume({ context: visualDesigner.context, subscribe: true })
  visualDesignerContext!: VisualDesignerContextType;

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (_changedProperties.has('scrollToStep') && this.scrollToStep) {
      scrollStepIntoView(this);
      isScrolledIntoView(this);
    }
  }

  async firstUpdated() {
    const { configurationId } = this.configuratorContext;
    if (configurationId) {
      await getPersonalisationData(this, configurationId);
      configuratorGlobal.updateGlobalProviderEvent(this, {
        ...this.configuratorContext,
        configurationId: '',
      });
    }
    this.announceSelectedPrintOptions();
  }

  private announceSelectedPrintOptions() {
    this.stepData?.printOptions?.forEach(printOption => {
      const selected = printOption.selectedPrintOption;
      if (!selected) return;

      const { colorCount, position, printTechnique, maxSurface } = selected;
      const printingNumber = printOption.title.match(/\d+/)?.[0] || '';

      const announcement = t('labels.printingOptionSelected', {
        number: printingNumber,
        position: position || '',
        maxSurface: maxSurface
          ? t('labels.maxPrintSurface', { surface: maxSurface })
          : '',
        technique: printTechnique || '',
        colors: colorCount
          ? t('labels.colors', { count: colorCount })
          : colorCount === null
            ? t('labels.fullColor')
            : '',
      });

      this.dispatchEvent(
        new CustomEvent('handle-announcement', {
          composed: true,
          detail: { announcement },
        }),
      );
    });
  }

  openPersonalisationDialog(
    selectedPrintOption: SelectedPrintOptionDto | undefined | null,
  ) {
    if (!selectedPrintOption) return;
    this.currentSelectedPrintOption = selectedPrintOption;
    this.fgPersonalisationDialogRef.value?.openDialog();
  }

  updateComponent(queryString: string | null | undefined) {
    if (queryString === null || queryString === undefined) {
      console.error('Missing query string');
      return;
    }
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: { option: { queryString } },
      }),
    );
  }

  /**
   * Check if the print option has personalisation data
   * If it does, remove the personalisation data for that print option.
   */
  removePersonalisation(code: string | null | undefined) {
    if (code === null || code === undefined) return;
    const selectedPrintOptionCode = code;
    if (!selectedPrintOptionCode) return;

    const personalisationData = personalisation.getDataWithCode(
      this.personalisationContext,
      selectedPrintOptionCode,
    );
    if (!personalisationData) return;

    this.personalisationData = personalisationData;
    personalisation.providerRemoveEvent(this, selectedPrintOptionCode);
  }

  showAccordionContent(stepData: PrintOptionCollapsibleStepDto) {
    if (this.stepData === undefined) return nothing;

    const { layout } = this.globalContext;

    return html`
      ${stepData.printOptions?.map(
        (printOption: PrintOptionCollapsibleDto, index: number) => {
          const { description, a11yTitle, subtitle } =
            getPrintOptionDisplayData(stepData, printOption);
          const selectedPrintOption = printOption.selectedPrintOption;
          const selectedPrintOptionCode = selectedPrintOption?.code;

          return html`
            <fg-accordion-wrapper slot="accordion-content">
              <fg-accordion-title
                part=${layout}
                slot="accordion-wrapper-header"
                index=${index}
                title=${printOption.title}
                a11yTitle=${a11yTitle}
                .stepType=${this.stepData?.type}
                .description=${description}
                .subtitle=${subtitle}
                .editQueryString=${printOption.editQueryString}
                .removeQueryString=${printOption.removeQueryString}
                .cancelQueryString=${printOption.cancelQueryString}
                .collapseQueryString=${printOption.collapseQueryString}
                .open=${printOption.open}
                .valid=${printOption.selectedPrintOption !== null ||
                stepData.unprinted}
                @action-edit=${() =>
                  this.updateComponent(printOption.editQueryString)}
                @action-delete=${() => {
                  this.updateComponent(printOption.removeQueryString);
                  this.removePersonalisation(
                    printOption?.selectedPrintOption?.code,
                  );
                }}
              >
                <span slot="title-button">
                  ${stepData.unprinted
                    ? html`
                        <fg-accordion-title-button
                          .collapseQueryString=${printOption.collapseQueryString}
                        ></fg-accordion-title-button>
                      `
                    : nothing}
                </span>
              </fg-accordion-title>
              ${stepData.valid &&
              !printOption.open &&
              !stepData.unprinted &&
              selectedPrintOptionCode
                ? html`
                    <fg-personalisation-status-bar
                      part=${layout}
                      .selectedPrintOptionCode=${selectedPrintOptionCode}
                      slot="accordion-wrapper-body"
                      .personalisationData=${personalisation.getDataWithCode(
                        this.personalisationContext,
                        selectedPrintOptionCode,
                      )}
                      .printOptionName=${printOption.title}
                      @open=${() => {
                        if (
                          this.configuratorContext.visualDesigner
                            ?.isVisualDesignerAvailable
                        ) {
                          const selectedPrintOptions = (
                            this.visualDesignerContext?.printOptions ?? []
                          ).flatMap(printPosition => {
                            const code = printPosition?.code;
                            return code ? [{ code, colorCount: 1 }] : [];
                          });

                          visualDesigner.providerUpdateEvent(this, {
                            ...this.visualDesignerContext,
                            selectedPrintOptions,
                          });
                        }

                        this.openPersonalisationDialog(selectedPrintOption);
                      }}
                      @remove=${() =>
                        personalisation.providerRemoveEvent(
                          this,
                          selectedPrintOptionCode,
                        )}
                    ></fg-personalisation-status-bar>
                  `
                : html`
                    <fg-collapsible-print-option
                      slot="accordion-wrapper-body"
                      index=${index}
                      .filters=${printOption.filters}
                      .open=${printOption.open}
                      .printOption=${printOption}
                      .totalOptions=${stepData.printOptions.length}
                      .unprintedQueryString=${printOption.unprintedQueryString}
                      .valid=${stepData.valid}
                    ></fg-collapsible-print-option>
                  `}
            </fg-accordion-wrapper>
            ${stepData.addPrintOptionQueryString !== null &&
            stepData.addPrintOptionQueryString !== undefined &&
            !stepData.unprinted &&
            index === 0
              ? html`
                  <fg-accordion-footer
                    slot="accordion-footer"
                    .queryString=${stepData.addPrintOptionQueryString}
                    buttonText="actions.addExtraPrintPosition"
                  ></fg-accordion-footer>
                `
              : nothing}
          `;
        },
      )}
    `;
  }

  render() {
    return html`
      <fg-accordion .stepData=${this.stepData}>
        ${this.stepData ? this.showAccordionContent(this.stepData) : nothing}
      </fg-accordion>

      <fg-personalisation
        ${ref(this.fgPersonalisationDialogRef)}
        .printOption=${this.currentSelectedPrintOption}
        .hasColorChoice=${this.currentSelectedPrintOption?.hasColorChoice}
        .isVisualDesignerAvailable=${Boolean(
          this.configuratorContext.visualDesigner?.isVisualDesignerAvailable,
        )}
        @personalisation-completed=${() => this.requestUpdate()}
      >
        <fg-print-option-summary
          slot="print-option-summary"
          .option=${this.currentSelectedPrintOption}
        ></fg-print-option-summary>
      </fg-personalisation>
    `;
  }

  static styles = [
    baseStyles,
    css`
      fg-personalisation-status-bar {
        display: block;
        margin-top: 0.5rem;
      }
    `,
  ];
}
